
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { db, FieldValue } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	RunnitNodeDef,
	RunnitNodeDefTag,
	READABLE_RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RUNNIT_NODE_DEF_TAG_TYPE_SORT,
} from '@run-diffusion/shared';
import BackButton from '@/components/base/BackButton.vue';

const NODE_DEF_BATCH_SIZE = 500;

export default Vue.extend({
	name: 'AdminManageToolTags',
	components: { BackButton },
	data () {
		return {
			loading: true as boolean,
			saving: false as boolean,
			nodeDefs: [] as RunnitNodeDef[],
			tags: [] as RunnitNodeDefTag[],
			expandedTypes: {} as Record<string, boolean>,
			selectedTagId: null as string | null,
			editingTagId: null as string | null,
			editLabel: '' as string,
			addingTagForType: null as string | null,
			newTagLabel: '' as string,
			creatingTag: false as boolean,
			addingTool: false as boolean,
			addNodeDefId: null as string | null,
		};
	},
	computed: {
		...mapState(['user']),
		nodeDefsMap (): Record<string, RunnitNodeDef> {
			return this.nodeDefs.reduce((acc: Record<string, RunnitNodeDef>, nd: RunnitNodeDef) => {
				acc[nd.id] = nd;
				return acc;
			}, {});
		},
		tagById (): Record<string, RunnitNodeDefTag> {
			return this.tags.reduce((acc: Record<string, RunnitNodeDefTag>, t: RunnitNodeDefTag) => {
				acc[t.id] = t;
				return acc;
			}, {});
		},
		tagsByTypeMap (): Record<string, RunnitNodeDefTag[]> {
			const map: Record<string, RunnitNodeDefTag[]> = {};
			for (const tag of this.tags) {
				const type = tag.type || 'INTERNAL';
				(map[type] = map[type] || []).push(tag);
			}
			Object.keys(map).forEach((type) => {
				map[type].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.label || '').localeCompare(b.label || ''));
			});
			return map;
		},
		sortedTagTypes (): string[] {
			// A card for every tag type (even empty), so the first tag of a type
			// can be added inline.
			return Object.keys(RUNNIT_NODE_DEF_TAG_TYPE).sort((a, b) => {
				const sa = (RUNNIT_NODE_DEF_TAG_TYPE_SORT as Record<string, number>)[a] ?? 999;
				const sb = (RUNNIT_NODE_DEF_TAG_TYPE_SORT as Record<string, number>)[b] ?? 999;
				return sa - sb || this.readableType(a).localeCompare(this.readableType(b));
			});
		},
		selectedTag (): RunnitNodeDefTag | null {
			return this.selectedTagId ? (this.tagById[this.selectedTagId] || null) : null;
		},
		inTagNodeDefs (): RunnitNodeDef[] {
			if (!this.selectedTagId) return [];
			const tagId = this.selectedTagId;
			const members = this.nodeDefs.filter((nd) => this.isInTag(nd, tagId));
			// Curated connections (tags[tagId] is an object with a numeric
			// sortOrder) come first, ascending; everyone else (legacy `true` /
			// no sortOrder) falls after, alphabetically by title — mirrors the
			// server's /tools?tag= ordering.
			return members.slice().sort((a, b) => {
				const sa = this.tagSortOrder(a, tagId);
				const sb = this.tagSortOrder(b, tagId);
				if (sa !== null && sb !== null) return sa - sb;
				if (sa !== null) return -1;
				if (sb !== null) return 1;
				return (a.title || '').localeCompare(b.title || '');
			});
		},
		candidateSelectItems (): { text: string; value: string }[] {
			if (!this.selectedTagId) return [];
			const tagId = this.selectedTagId;
			return this.nodeDefs
				.filter((nd) => !this.isInTag(nd, tagId))
				.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
				.map((nd) => ({ text: nd.title || nd.id, value: nd.id }));
		},
	},
	mounted () {
		this.initialize();
	},
	methods: {
		...mapActions(['updateSnackbar']),
		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		readableType (type: string): string {
			return (READABLE_RUNNIT_NODE_DEF_TAG_TYPE as Record<string, string>)[type] || type || '';
		},
		isInTag (nodeDef: RunnitNodeDef, tagId: string): boolean {
			return !!(nodeDef.tags && nodeDef.tags[tagId]);
		},
		// The tool's explicit display order within `tagId`, or null when the
		// connection is unordered (legacy `true`, or an object without sortOrder).
		tagSortOrder (nodeDef: RunnitNodeDef, tagId: string): number | null {
			const conn: any = nodeDef.tags && (nodeDef.tags as any)[tagId];
			return conn && typeof conn === 'object' && typeof conn.sortOrder === 'number'
				? conn.sortOrder
				: null;
		},
		toggleType (type: string) {
			this.$set(this.expandedTypes, type, !this.expandedTypes[type]);
		},
		selectTag (tag: RunnitNodeDefTag) {
			if (this.editingTagId === tag.id) return;
			this.selectedTagId = tag.id;
			this.addingTool = false;
			this.addNodeDefId = null;
			// Collapse every type group except the one this tag belongs to.
			this.expandedTypes = { [tag.type]: true };
		},
		async initialize () {
			this.loading = true;
			try {
				await Promise.all([this.fetchNodeDefs(), this.fetchTags()]);
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to load tools/tags.');
			} finally {
				this.loading = false;
			}
		},
		async fetchNodeDefs () {
			const out: RunnitNodeDef[] = [];
			let lastDoc: any = null;
			// eslint-disable-next-line no-constant-condition
			while (true) {
				let query: any = db.collection('runnitNodeDefs')
					.where('isDeleted', '==', false)
					.limit(NODE_DEF_BATCH_SIZE);
				if (lastDoc) query = query.startAfter(lastDoc);
				const snap = await query.get();
				snap.docs.forEach((doc: any) => out.push({ ...doc.data(), get id () { return doc.id; } } as RunnitNodeDef));
				if (snap.docs.length < NODE_DEF_BATCH_SIZE) break;
				lastDoc = snap.docs[snap.docs.length - 1];
			}
			this.nodeDefs = out;
		},
		async fetchTags () {
			const snap = await db.collection('runnitNodeDefTags').where('isDeleted', '==', false).get();
			this.tags = snap.docs.map((doc: any) => ({ ...doc.data(), get id () { return doc.id; } } as RunnitNodeDefTag));
		},

		// ── Tag CRUD + reorder ──
		startAddTag (type: string) {
			this.$set(this.expandedTypes, type, true);
			this.addingTagForType = type;
			this.newTagLabel = '';
			this.$nextTick(() => {
				const ref: any = (this.$refs as any)[`addInput-${type}`];
				const el = Array.isArray(ref) ? ref[0] : ref;
				if (el && el.focus) el.focus();
			});
		},
		cancelAddTag () {
			this.addingTagForType = null;
			this.newTagLabel = '';
		},
		async createTag (type: string) {
			const label = (this.newTagLabel || '').trim();
			if (!label) {
				this.snack(SNACKBAR_STATUS.WARN, 'Enter a tag label.');
				return;
			}
			this.creatingTag = true;
			try {
				const sortOrder = (this.tagsByTypeMap[type] || []).length;
				const ref = await db.collection('runnitNodeDefTags').add({
					type,
					label,
					sortOrder,
					isDeleted: false,
					deletedAt: null,
					createdAt: new Date(),
				});
				await this.fetchTags();
				this.addingTagForType = null;
				this.newTagLabel = '';
				this.selectTag({ ...(this.tagById[ref.id] as any), id: ref.id });
				this.snack(SNACKBAR_STATUS.SUCCESS, `Created tag "${label}".`);
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to create tag.');
			} finally {
				this.creatingTag = false;
			}
		},
		startEdit (tag: RunnitNodeDefTag) {
			this.editingTagId = tag.id;
			this.editLabel = tag.label || '';
		},
		cancelEdit () {
			this.editingTagId = null;
		},
		async saveEdit (tag: RunnitNodeDefTag) {
			const label = (this.editLabel || '').trim();
			if (!label) {
				this.snack(SNACKBAR_STATUS.WARN, 'Label cannot be empty.');
				return;
			}
			this.saving = true;
			try {
				await db.doc(`runnitNodeDefTags/${tag.id}`).update({ label });
				await this.fetchTags();
				this.editingTagId = null;
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to save the tag.');
			} finally {
				this.saving = false;
			}
		},
		async deleteTag (tag: RunnitNodeDefTag) {
			// eslint-disable-next-line no-alert
			if (!window.confirm(`Delete tag "${tag.label}"? Tools keep their other tags.`)) return;
			this.saving = true;
			try {
				await db.doc(`runnitNodeDefTags/${tag.id}`).update({ isDeleted: true, deletedAt: new Date() });
				if (this.selectedTagId === tag.id) this.selectedTagId = null;
				await this.fetchTags();
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to delete the tag.');
			} finally {
				this.saving = false;
			}
		},
		async switchTagSortOrder (tagA: RunnitNodeDefTag, tagB: RunnitNodeDefTag) {
			if (!tagB) return;
			this.saving = true;
			try {
				await Promise.all([
					db.doc(`runnitNodeDefTags/${tagA.id}`).update({ sortOrder: tagB.sortOrder ?? 0 }),
					db.doc(`runnitNodeDefTags/${tagB.id}`).update({ sortOrder: tagA.sortOrder ?? 0 }),
				]);
				await this.fetchTags();
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to reorder tags.');
			} finally {
				this.saving = false;
			}
		},

		// ── Tool membership + per-tag tool order ──
		currentOrderIds (): string[] {
			return this.inTagNodeDefs.map((nd) => nd.id);
		},
		startAddTool () {
			this.addingTool = true;
			this.addNodeDefId = null;
		},
		async moveToolUp (index: number) {
			if (index <= 0) return;
			const ids = this.currentOrderIds();
			[ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
			await this.persistOrder(ids);
		},
		async moveToolDown (index: number) {
			const ids = this.currentOrderIds();
			if (index >= ids.length - 1) return;
			[ids[index + 1], ids[index]] = [ids[index], ids[index + 1]];
			await this.persistOrder(ids);
		},
		async onAddTool (nodeDefId: string | null) {
			if (!nodeDefId || !this.selectedTagId) return;
			const nodeDef = this.nodeDefsMap[nodeDefId];
			if (!nodeDef) return;
			// Join as an unordered `true` member; it sorts after the curated tools
			// (or alphabetically when the tag isn't curated). Use up/down to place
			// it — that's what assigns sortOrder. We deliberately DON'T renumber
			// here, so adding a tool never promotes an otherwise-alphabetical tag
			// into a curated one.
			try {
				await this.setMembership(nodeDef, this.selectedTagId, true);
			} catch (err) { /* setMembership already surfaced the error */ }
			this.addNodeDefId = null;
			this.addingTool = false;
		},
		async removeTool (nodeDef: RunnitNodeDef) {
			if (!this.selectedTagId) return;
			// Just drop membership; leave the other members' sortOrder as-is (a gap
			// is harmless — order is preserved, and the next up/down re-densifies).
			// Not renumbering here means removing a tool never promotes the
			// remaining alphabetical members into a curated order.
			try {
				await this.setMembership(nodeDef, this.selectedTagId, false);
			} catch (err) { /* setMembership already surfaced the error */ }
		},
		async persistOrder (orderedIds: string[]) {
			if (!this.selectedTagId) return;
			const tagId = this.selectedTagId;
			this.saving = true;
			try {
				// Dense renumber-all: write sortOrder 1..N into each member's
				// connection. Use a dot-path field update (`tags.<id>`) so we touch
				// ONLY this tag's connection and never overwrite the tool's whole
				// tags map from a possibly-stale in-memory snapshot — a concurrent
				// change to another tag on the same tool is preserved. The conn
				// object is merge-preserved (sortOrder + future metadata). One batch.
				const batch = db.batch();
				const applied: { nodeDef: RunnitNodeDef; conn: Record<string, any> }[] = [];
				orderedIds.forEach((id, i) => {
					const nodeDef = this.nodeDefsMap[id];
					if (!nodeDef) return;
					const existing: any = nodeDef.tags && (nodeDef.tags as any)[tagId];
					const conn = (existing && typeof existing === 'object') ? { ...existing } : {};
					conn.sortOrder = i + 1;
					batch.update(db.doc(`runnitNodeDefs/${id}`), { [`tags.${tagId}`]: conn });
					applied.push({ nodeDef, conn });
				});
				await batch.commit();
				applied.forEach(({ nodeDef, conn }) => {
					if (!nodeDef.tags) this.$set(nodeDef, 'tags', {});
					this.$set(nodeDef.tags as any, tagId, conn);
				});
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to save the tool order.');
			} finally {
				this.saving = false;
			}
		},
		async setMembership (nodeDef: RunnitNodeDef, tagId: string, add: boolean) {
			this.saving = true;
			try {
				// Dot-path field updates so we touch ONLY this tag's membership and
				// its tag-type — never rewrite the tool's whole tags/tagTypes maps
				// from a possibly-stale snapshot (which would clobber a concurrent
				// edit to another tag on the same tool). Adding preserves any
				// existing connection value; we only set `true` when the tag is new.
				const tag = this.tagById[tagId];
				const type = tag?.type;
				const update: Record<string, any> = {};
				if (add) {
					if (!(nodeDef.tags && (nodeDef.tags as any)[tagId])) update[`tags.${tagId}`] = true;
					if (type) update[`tagTypes.${type}`] = true;
				} else {
					update[`tags.${tagId}`] = FieldValue.delete();
					// Drop the tag-type only when no OTHER current tag shares it.
					const sharesType = !!type && Object.keys(nodeDef.tags || {})
						.some((tid) => tid !== tagId && this.tagById[tid]?.type === type);
					if (type && !sharesType) update[`tagTypes.${type}`] = FieldValue.delete();
				}
				if (Object.keys(update).length) await db.doc(`runnitNodeDefs/${nodeDef.id}`).update(update);
				// Mirror into local reactive state.
				if (!nodeDef.tags) this.$set(nodeDef, 'tags', {});
				if (!nodeDef.tagTypes) this.$set(nodeDef, 'tagTypes', {});
				if (add) {
					if (!(nodeDef.tags as any)[tagId]) this.$set(nodeDef.tags as any, tagId, true);
					if (type) this.$set(nodeDef.tagTypes as any, type, true);
				} else {
					this.$delete(nodeDef.tags as any, tagId);
					const stillShares = !!type && Object.keys(nodeDef.tags || {})
						.some((tid) => this.tagById[tid]?.type === type);
					if (type && !stillShares) this.$delete(nodeDef.tagTypes as any, type);
				}
			} catch (err) {
				this.snack(SNACKBAR_STATUS.ERROR, 'Failed to update the tool\'s tags.');
				throw err;
			} finally {
				this.saving = false;
			}
		},
		snack (status: string, message: string) {
			this.updateSnackbar({ status, message, show: true });
		},
	},
});
